export type PricingTier = {
  slug: string;
  name: string;
  priceFrom: number;
  currency: 'EUR';
  timeline: string;
  summary: string;
  features: string[];
  highlighted?: boolean;
};

export type PricingGroup = {
  serviceSlug: 'logo-design' | 'website-design' | 'website-development';
  title: string;
  tiers: PricingTier[];
};

export const pricingGroups: PricingGroup[] = [
  {
    serviceSlug: 'logo-design',
    title: 'Logo design',
    tiers: [
      {
        slug: 'logo-starter',
        name: 'Starter',
        priceFrom: 700,
        currency: 'EUR',
        timeline: '2 weeks',
        summary: 'A single clean mark and the files you need to use it.',
        features: [
          'One direction',
          'Two rounds of revisions',
          'SVG, PNG, PDF delivery',
          'Color palette',
          'Typography pairing',
        ],
      },
      {
        slug: 'logo-studio',
        name: 'Studio',
        priceFrom: 1500,
        currency: 'EUR',
        timeline: '3 weeks',
        summary: 'Three directions, a proper brand kit, room to negotiate.',
        features: [
          'Three directions',
          'Four rounds of revisions',
          'Primary and secondary lockups',
          'Full brand kit (colors, type, rules)',
          'Usage guide (do / do not)',
          'Favicon and social avatar',
        ],
        highlighted: true,
      },
      {
        slug: 'logo-identity',
        name: 'Identity',
        priceFrom: 2800,
        currency: 'EUR',
        timeline: '4-5 weeks',
        summary: 'A full identity - logo, system, and the document that protects it.',
        features: [
          'Everything in Studio',
          'Stationery templates (business card, letterhead, email signature)',
          'Social templates for Instagram and LinkedIn',
          'Brand guidelines PDF (20+ pages)',
          'One year of minor revisions included',
        ],
      },
    ],
  },
  {
    serviceSlug: 'website-design',
    title: 'Website design',
    tiers: [
      {
        slug: 'design-landing',
        name: 'Landing',
        priceFrom: 1200,
        currency: 'EUR',
        timeline: '2 weeks',
        summary: 'One page, up to eight sections, responsive from the start.',
        features: [
          'One page',
          'Up to eight sections',
          'Desktop and mobile comps',
          'Design system basics',
          'One revision round',
        ],
      },
      {
        slug: 'design-starter',
        name: 'Starter',
        priceFrom: 2400,
        currency: 'EUR',
        timeline: '3-4 weeks',
        summary: 'Five-page marketing site with a design system you can grow into.',
        features: [
          'Up to five unique pages',
          'Responsive 375 / 768 / 1440',
          'Design system library',
          'Interactive prototype',
          'Two revision rounds',
        ],
        highlighted: true,
      },
      {
        slug: 'design-business',
        name: 'Business',
        priceFrom: 4400,
        currency: 'EUR',
        timeline: '5-6 weeks',
        summary: 'Ten pages, custom illustration selection, full developer handoff.',
        features: [
          'Up to ten unique pages',
          'Custom illustration curation',
          'Responsive 375 to 1920',
          'Prototype + user flows',
          'Three revision rounds',
          'Developer handoff document',
        ],
      },
    ],
  },
  {
    serviceSlug: 'website-development',
    title: 'Website development',
    tiers: [
      {
        slug: 'dev-landing',
        name: 'Landing',
        priceFrom: 1600,
        currency: 'EUR',
        timeline: '1-2 weeks',
        summary: 'One page, built in React, deployed, ready to ship.',
        features: [
          'React + Vite + TypeScript',
          'One page',
          'Production build',
          'Deployment to your host',
          'One month of defect fixes',
        ],
      },
      {
        slug: 'dev-starter',
        name: 'Starter',
        priceFrom: 3200,
        currency: 'EUR',
        timeline: '3-4 weeks',
        summary: 'Up to five pages, optional CMS, real analytics setup.',
        features: [
          'Up to five pages',
          'Contact form with validation',
          'Basic SEO setup',
          'Optional headless CMS',
          'Two months of defect fixes',
        ],
        highlighted: true,
      },
      {
        slug: 'dev-business',
        name: 'Business',
        priceFrom: 5600,
        currency: 'EUR',
        timeline: '5-7 weeks',
        summary: 'Ten pages, CMS, performance tuning, three months of support.',
        features: [
          'Up to ten pages',
          'Headless CMS integration',
          'SEO deep pack (sitemap, OG, schema.org)',
          'Performance tuning (Lighthouse 95+)',
          'Analytics integration',
          'Three months of defect fixes',
        ],
      },
    ],
  },
];

export type Bundle = {
  slug: string;
  name: string;
  priceFrom: number;
  priceOriginal: number;
  currency: 'EUR';
  description: string;
  components: string[];
};

export const bundles: Bundle[] = [
  {
    slug: 'launch',
    name: 'Launch',
    priceFrom: 4000,
    priceOriginal: 4500,
    currency: 'EUR',
    description: 'Smallest path from zero to a live site.',
    components: ['Logo Starter', 'Web Design Landing', 'Dev Landing'],
  },
  {
    slug: 'studio',
    name: 'Studio',
    priceFrom: 6400,
    priceOriginal: 7300,
    currency: 'EUR',
    description: 'Five-page site, a proper brand kit, built to last.',
    components: ['Logo Studio', 'Web Design Starter', 'Dev Starter'],
  },
  {
    slug: 'full-identity',
    name: 'Full Identity',
    priceFrom: 11500,
    priceOriginal: 12800,
    currency: 'EUR',
    description: 'A complete identity and a ten-page site without compromise.',
    components: ['Logo Identity', 'Web Design Business', 'Dev Business'],
  },
];

export function formatEur(value: number): string {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

export const paymentTerms = {
  split: '50% to start, 50% before launch',
  currency: 'EUR',
  vat: 'Cyprus VAT (19%) applies to EU clients without a valid VAT ID. Non-EU clients are zero-rated. VAT is added on top of the prices shown.',
  invoicing: 'Invoice at each milestone. Payable within 14 days by bank transfer.',
};

export const notIncludedInAnyTier = [
  'Stock photography and illustration licenses',
  'Custom copywriting or content strategy',
  'Custom photography or video production',
  'Third-party service fees (CMS, analytics, email, hosting)',
  'Translations or localisation',
  'Print production and shipping',
];

export type AddOn = {
  name: string;
  priceNote: string;
  description: string;
};

export const addOns: AddOn[] = [
  { name: 'Extra page', priceNote: '€400', description: 'Design and development of one additional unique page.' },
  { name: 'Extra revision round', priceNote: '€150', description: 'One full revision cycle beyond the tier limit.' },
  { name: 'Rush delivery', priceNote: '+30%', description: 'Reduces timeline by up to a third. Subject to team availability.' },
  { name: 'CMS integration', priceNote: 'from €800', description: 'Sanity or Payload setup so non-developers can edit content.' },
];
